import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { User, Shield, School, Database, Calendar, Check, Sparkles } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { ProgressBar } from '../ui/Progress';
import { UserStats } from '../../types';
import { GroupId, StartingPeriod, STARTING_PERIODS } from '../../config/systemConfig';
import { resolvePhaseByDate, getAutoPhaseLabel } from '../../utils/dateBasedPlacement';
interface ProfileSectionProps {
  stats: UserStats;
  selectedGroup: GroupId;
  startingPeriod: StartingPeriod; // Effective period
  onSetStartingPeriod: (period: StartingPeriod | null) => void; // Accepts null for Auto
  manualPeriod?: StartingPeriod | null; // New prop to track if manually set
}
export function ProfileSection({
  stats,
  selectedGroup,
  startingPeriod,
  onSetStartingPeriod,
  manualPeriod // We need to pass this from parent or useAppStore
}: ProfileSectionProps) {
  const progressToNextLevel = stats.xp % 250 / 250 * 100;
  const xpToNextLevel = 250 - stats.xp % 250;
  const autoPhase = resolvePhaseByDate();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 12
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="space-y-6" variants={containerVariants} initial="hidden" animate="show" data-id="element-1585">
      {/* Main Profile Card */}
      <motion.div variants={itemVariants} data-id="element-1586">
        <Card className="p-6 md:p-8" data-id="element-1587">
          <div className="flex flex-col sm:flex-row items-center gap-6" data-id="element-1588">
            {/* Avatar */}
            <div className="relative flex-shrink-0" data-id="element-1589">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center text-white text-4xl font-bold shadow-lg shadow-indigo-200/50" data-id="element-1590">
                {stats.name.charAt(0)}
              </div>
              <div className="absolute -bottom-1 -right-1 bg-white rounded-full p-0.5 shadow-sm" data-id="element-1591">
                <div className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-1592">
                  Lvl {stats.level}
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left" data-id="element-1593">
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-1594">
                {stats.name}
              </h2>
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-2" data-id="element-1595">
                <Badge variant="info" icon={<School className="w-3 h-3" data-id="element-1597" />} data-id="element-1596">
                  Groep {selectedGroup}
                </Badge>
                <Badge variant="default" icon={<Shield className="w-3 h-3" data-id="element-1599" />} data-id="element-1598">
                  Leerling
                </Badge>
              </div>

              {/* XP Progress */}
              <div className="mt-4 max-w-xs" data-id="element-1600">
                <div className="flex justify-between text-xs text-slate-500 mb-1" data-id="element-1601">
                  <span className="font-medium" data-id="element-1602">Level {stats.level}</span>
                  <span data-id="element-1603">
                    {stats.xp} XP • nog {xpToNextLevel} tot level{' '}
                    {stats.level + 1}
                  </span>
                </div>
                <ProgressBar value={progressToNextLevel} height={6} color="bg-indigo-500" data-id="element-1604" />
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Starting Period Selector */}
      <motion.div variants={itemVariants} data-id="element-1605">
        <Card className="p-6" data-id="element-1606">
          <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2" data-id="element-1607">
            <Calendar className="w-4 h-4 text-indigo-500" data-id="element-1608" />
            Wanneer ben je ingestapt?
          </h3>
          <p className="text-sm text-slate-500 mb-5" data-id="element-1609">
            Standaard bepalen we dit automatisch op basis van de datum. Je kunt
            dit hieronder aanpassen.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-1610">
            {/* Auto Option */}
            <button onClick={() => onSetStartingPeriod(null)} className={`
                relative p-4 rounded-xl border-2 transition-all text-center flex flex-col items-center justify-center
                ${!manualPeriod ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-slate-100 bg-white hover:border-indigo-200 hover:bg-slate-50'}
              `} data-id="element-1611">
              {!manualPeriod && <div className="absolute top-2 right-2" data-id="element-1612">
                  <Check className="w-4 h-4 text-indigo-600" data-id="element-1613" />
                </div>}
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${!manualPeriod ? 'bg-indigo-200 text-indigo-700' : 'bg-slate-100 text-slate-400'}`} data-id="element-1614">
                <Sparkles className="w-5 h-5" data-id="element-1615" />
              </div>
              <div className={`font-bold text-sm ${!manualPeriod ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-1616">
                Automatisch
              </div>
              <div className="text-[10px] text-slate-400 mt-1" data-id="element-1617">
                {getAutoPhaseLabel()}
              </div>
            </button>

            {/* Manual Options */}
            {STARTING_PERIODS.map(period => {
            const isActive = manualPeriod === period.id;
            return <button key={period.id} onClick={() => onSetStartingPeriod(period.id)} className={`
                    relative p-4 rounded-xl border-2 transition-all text-center
                    ${isActive ? 'border-indigo-500 bg-indigo-50 shadow-sm' : 'border-slate-100 bg-white hover:border-indigo-200 hover:bg-slate-50'}
                  `} data-id="element-1618">
                  {isActive && <div className="absolute top-2 right-2" data-id="element-1619">
                      <Check className="w-4 h-4 text-indigo-600" data-id="element-1620" />
                    </div>}
                  <div className={`text-2xl mb-2 ${isActive ? '' : 'grayscale opacity-60'}`} data-id="element-1621">
                    {period.id === 'B' ? '🌱' : period.id === 'M' ? '☀️' : '🎓'}
                  </div>
                  <div className={`font-bold text-sm ${isActive ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-1622">
                    {period.label}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5" data-id="element-1623">
                    {period.description}
                  </div>
                </button>;
          })}
          </div>

          <div className="mt-4 bg-slate-50 p-3 rounded-lg border border-slate-100" data-id="element-1624">
            <p className="text-xs text-slate-600 flex items-center gap-2" data-id="element-1625">
              <span className="font-bold text-indigo-600" data-id="element-1626">Huidige start:</span>
              Niveau {startingPeriod}
              {selectedGroup}
              {manualPeriod ? ' (Handmatig ingesteld)' : ' (Automatisch bepaald)'}
            </p>
          </div>
        </Card>
      </motion.div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1627">
        <motion.div variants={itemVariants} data-id="element-1628">
          <Card className="p-6 h-full" data-id="element-1629">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1630">
              <User className="w-4 h-4 text-slate-400" data-id="element-1631" />
              Persoonlijke Gegevens
            </h3>
            <div className="space-y-3" data-id="element-1632">
              {[{
              label: 'Naam',
              value: stats.name
            }, {
              label: 'Rol',
              value: 'Leerling'
            }, {
              label: 'Schooljaar',
              value: '2025 – 2026'
            }].map(row => <div key={row.label} className="flex justify-between items-center py-2.5 border-b border-slate-50 last:border-0" data-id="element-1633">
                  <span className="text-sm text-slate-500" data-id="element-1634">{row.label}</span>
                  <span className="text-sm font-medium text-slate-900" data-id="element-1635">
                    {row.value}
                  </span>
                </div>)}
            </div>
          </Card>
        </motion.div>

        <motion.div variants={itemVariants} data-id="element-1636">
          <Card className="p-6 h-full" data-id="element-1637">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-1638">
              <Database className="w-4 h-4 text-slate-400" data-id="element-1639" />
              Opslag &amp; Data
            </h3>
            <div className="space-y-3" data-id="element-1640">
              {[{
              label: 'Opslagtype',
              value: 'Lokaal (browser)'
            }, {
              label: 'Huidige groep',
              value: `Groep ${selectedGroup}`
            }, {
              label: 'Startpunt',
              value: `${startingPeriod}${selectedGroup}`
            }, {
              label: 'Modus',
              value: manualPeriod ? 'Handmatig' : 'Auto'
            }].map(row => <div key={row.label} className="flex justify-between items-center py-2.5 border-b border-slate-50 last:border-0" data-id="element-1641">
                  <span className="text-sm text-slate-500" data-id="element-1642">{row.label}</span>
                  <span className="text-sm font-medium text-slate-900" data-id="element-1643">
                    {row.value}
                  </span>
                </div>)}
            </div>
            <p className="text-xs text-slate-400 mt-4" data-id="element-1644">
              Je voortgang wordt lokaal in je browser opgeslagen.
            </p>
          </Card>
        </motion.div>
      </div>
    </motion.div>;
}